import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../services/api';
import { Sablona } from '../types';

interface PopisAllLangs {
  cz: string;
  en: string;
  de: string;
}

interface SablonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPopis: PopisAllLangs;
  onApply: (newPopis: PopisAllLangs) => void;
}

type ApplyMode = 'replace' | 'prepend' | 'append';

// Načtení popis pro všechny jazyky pro danou šablonu
interface SablonaAllLangs {
  id: number;
  nazev: string;
  popis_cz: string;
  popis_en: string;
  popis_de: string;
}

const SablonaModal: React.FC<SablonaModalProps> = ({ isOpen, onClose, currentPopis, onApply }) => {
  const [sablony, setSablony] = useState<SablonaAllLangs[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const fetchSablony = async () => {
      setLoading(true);
      setError(null);
      try {
        const [cz, en, de] = await Promise.all([
          apiClient.getSablony('cz'),
          apiClient.getSablony('en'),
          apiClient.getSablony('de'),
        ]);

        // Mapa popis dle jazyka
        const enMap = new Map<number, string>(en.map((s: Sablona) => [s.id, s.popis || '']));
        const deMap = new Map<number, string>(de.map((s: Sablona) => [s.id, s.popis || '']));

        const merged: SablonaAllLangs[] = cz.map((s: Sablona) => ({
          id: s.id,
          nazev: s.nazev,
          popis_cz: s.popis || '',
          popis_en: enMap.get(s.id) || s.popis || '',
          popis_de: deMap.get(s.id) || s.popis || '',
        }));

        setSablony(merged);
      } catch (err: any) {
        setError('Chyba při načítání šablon: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchSablony();
  }, [isOpen]);

  const filteredSablony = useMemo(() => {
    if (!search.trim()) return sablony;
    const q = search.trim().toLowerCase();
    return sablony.filter(
      s =>
        s.nazev.toLowerCase().includes(q) ||
        s.popis_cz.toLowerCase().includes(q)
    );
  }, [sablony, search]);

  const handleApply = (sablona: SablonaAllLangs, mode: ApplyMode) => {
    const apply = (current: string, template: string): string => {
      if (!template) return current;
      switch (mode) {
        case 'replace':
          return template;
        case 'prepend':
          return current.trim()
            ? template + '\n\n' + current
            : template;
        case 'append':
          return current.trim()
            ? current + '\n\n' + template
            : template;
      }
    };

    onApply({
      cz: apply(currentPopis.cz, sablona.popis_cz),
      en: apply(currentPopis.en, sablona.popis_en),
      de: apply(currentPopis.de, sablona.popis_de),
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>📋 Šablony popisů</h2>
          <button style={styles.closeButton} onClick={onClose} title="Zavřít">×</button>
        </div>

        {/* Search */}
        <div style={styles.searchBar}>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="🔍 Hledat šablonu podle názvu nebo obsahu..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          {search && (
            <button style={styles.clearSearch} onClick={() => setSearch('')} title="Vymazat hledání">×</button>
          )}
        </div>

        {/* Help text */}
        <div style={styles.helpText}>
          Kliknutím na tlačítko se šablona aplikuje do <strong>všech jazykových záložek</strong> najednou.
        </div>

        {/* List */}
        <div style={styles.list}>
          {loading && (
            <div style={styles.centerMsg}>Načítám šablony...</div>
          )}
          {error && (
            <div style={styles.errorMsg}>{error}</div>
          )}
          {!loading && !error && filteredSablony.length === 0 && (
            <div style={styles.centerMsg}>
              {search ? 'Žádná šablona neodpovídá hledání.' : 'Žádné šablony nenalezeny.'}
            </div>
          )}
          {!loading && !error && filteredSablony.map(sablona => (
            <div key={sablona.id} style={styles.item}>
              <div style={styles.itemContent}>
                <div style={styles.itemName}>{sablona.nazev}</div>
                {sablona.popis_cz && (
                  <div style={styles.itemPopis}>{sablona.popis_cz}</div>
                )}
              </div>
              <div style={styles.itemActions}>
                <button
                  style={styles.btnReplace}
                  onClick={() => handleApply(sablona, 'replace')}
                  title="Nahradí veškerý stávající text popisu textem šablony"
                >
                  Nahradit celé
                </button>
                <button
                  style={styles.btnPrepend}
                  onClick={() => handleApply(sablona, 'prepend')}
                  title="Vloží text šablony na začátek popisu"
                >
                  Předřadit
                </button>
                <button
                  style={styles.btnAppend}
                  onClick={() => handleApply(sablona, 'append')}
                  title="Přidá text šablony na konec popisu"
                >
                  Zařadit
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <span style={styles.footerCount}>
            {!loading && !error && `${filteredSablony.length} / ${sablony.length} šablon`}
          </span>
          <button style={styles.cancelButton} onClick={onClose}>Zavřít</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0 as const,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#666',
    lineHeight: 1,
    padding: '0 4px',
  },
  searchBar: {
    padding: '12px 20px',
    borderBottom: '1px solid #eee',
    position: 'relative' as const,
    flexShrink: 0 as const,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  searchInput: {
    flex: 1,
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    outline: 'none',
  },
  clearSearch: {
    background: '#888',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: '22px',
    height: '22px',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0 as const,
  },
  helpText: {
    padding: '8px 20px',
    fontSize: '12px',
    color: '#888',
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #eee',
    flexShrink: 0 as const,
  },
  list: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '8px 12px',
  },
  centerMsg: {
    padding: '30px',
    textAlign: 'center' as const,
    color: '#888',
    fontSize: '14px',
  },
  errorMsg: {
    margin: '10px',
    padding: '12px 16px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    fontSize: '14px',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '6px',
    border: '1px solid #e8e8e8',
    backgroundColor: '#fafafa',
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontWeight: 'bold' as const,
    fontSize: '14px',
    color: '#333',
    marginBottom: '4px',
  },
  itemPopis: {
    fontSize: '13px',
    color: '#666',
    whiteSpace: 'pre-line' as const,
    maxHeight: '80px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemActions: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '5px',
    flexShrink: 0 as const,
  },
  btnReplace: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#e53935',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  btnPrepend: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#1976D2',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  btnAppend: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#388E3C',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  footer: {
    padding: '12px 20px',
    borderTop: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0 as const,
  },
  footerCount: {
    fontSize: '13px',
    color: '#888',
  },
  cancelButton: {
    padding: '8px 20px',
    fontSize: '14px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export default SablonaModal;
